import React, { useState } from "react";
import { Box, IconButton, Typography, Stack, Paper, Button, Tabs, Tab } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import Permission from "./Permission";
import Setup from "./Setup";
import ConsultationTypeList from "./ConsultationTypeList";

const SettingsView = ({
  onBack,
  permissions,
  calendarSettings,
  onSettingsUpdate,
  roleName
}) => {
  const [activeTab, setActiveTab] = useState(() => {
    if (calendarSettings?.calendarProvider) return calendarSettings.calendarProvider;
    if (calendarSettings?.isCalendarGoogleEnabled !== false) return "google";
    if (calendarSettings?.isCalendarOutlookEnabled !== false) return "outlook";
    return "google";
  });

  const setupRef = React.useRef(null);

  const allConsultationTypes = calendarSettings?.consultationTypes || calendarSettings?.googleConfig?.consultationTypes || [];
  const defaultName = (activeTab === 'outlook' ? calendarSettings?.outlookConfig?.name : calendarSettings?.googleConfig?.name) ||
    calendarSettings?.defaultConsultationType?.name ||
    calendarSettings?.name ||
    "General";
  const defaultDescription = (activeTab === 'outlook' ? calendarSettings?.outlookConfig?.description : calendarSettings?.googleConfig?.description) ||
    calendarSettings?.defaultConsultationType?.description ||
    calendarSettings?.description ||
    "";

  // Logged-in User Role & Scoping Check
  const loggedInUserStr = localStorage.getItem("user");
  const loggedInUser = loggedInUserStr ? JSON.parse(loggedInUserStr) : null;
  const userRole = (loggedInUser?.roleDetails?.name || roleName || loggedInUser?.role || "").toLowerCase();
  
  // Super Admin AND Store Admin see ALL consultation types + GENERAL (Default Global Settings)
  const isSuperOrStoreAdmin = userRole === "super admin" || 
                              userRole === "store admin" || 
                              userRole === "admin" || 
                              userRole === "super_admin" || 
                              userRole === "store_admin";

  const userEmail = (loggedInUser?.email || "").toLowerCase();
  const userId = (loggedInUser?._id || loggedInUser?.id || "").toString();

  // Filter Consultation Types assigned to THIS logged-in user / role
  const visibleConsultationTypesWithIndex = allConsultationTypes.map((type, originalIdx) => {
    if (isSuperOrStoreAdmin) return { type, originalIdx };

    const managerEmail = (type.assignedManagerEmail || "").toLowerCase();
    const managerId = (type.assignedManagerId || "").toString();
    const managerRole = (type.assignedManagerRole || "").toLowerCase();
    const calEmail = (type.calendarEmail || "").toLowerCase();

    const isMatch = (userEmail && (managerEmail === userEmail || calEmail === userEmail)) ||
                    (userId && managerId === userId) ||
                    (userRole && managerRole === userRole);

    return isMatch ? { type, originalIdx } : null;
  }).filter(Boolean);

  // Show Default Global Settings tab & card for Super Admin, Store Admin, or if no specific types assigned
  const showDefaultSettings = isSuperOrStoreAdmin || visibleConsultationTypesWithIndex.length === 0;

  // Initial selected tab index:
  // If showDefaultSettings is true, default to 0 (GENERAL)
  // Else default to first assigned consultation type's originalIdx + 1
  const initialTypeIndex = React.useMemo(() => {
    if (showDefaultSettings) return 0;
    return visibleConsultationTypesWithIndex.length > 0 ? (visibleConsultationTypesWithIndex[0].originalIdx + 1) : 0;
  }, [showDefaultSettings, visibleConsultationTypesWithIndex]);

  const [viewingTypeIndex, setViewingTypeIndex] = useState(initialTypeIndex);

  // Sync viewingTypeIndex if initialTypeIndex changes
  React.useEffect(() => {
    if (!showDefaultSettings && viewingTypeIndex === 0 && visibleConsultationTypesWithIndex.length > 0) {
      setViewingTypeIndex(visibleConsultationTypesWithIndex[0].originalIdx + 1);
    }
  }, [showDefaultSettings, visibleConsultationTypesWithIndex, viewingTypeIndex]);

  const handleOpenAddModal = () => {
    if (setupRef.current && typeof setupRef.current.openAddTypeModal === 'function') {
      setupRef.current.openAddTypeModal();
    }
  };

  const selectedType = (viewingTypeIndex !== null && viewingTypeIndex > 0)
    ? allConsultationTypes[viewingTypeIndex - 1]
    : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "auto" }}>
      {/* PAGE 1: Consultation Types List Grid */}
      {viewingTypeIndex === null ? (
        <Box sx={{ p: 3, maxWidth: 1200, margin: "0 auto", width: "100%" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton onClick={() => setViewingTypeIndex(initialTypeIndex)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0d3559' }}>
                Consultation Types Dashboard
              </Typography>
            </Stack>

            <Button
              variant="outlined"
              onClick={() => setViewingTypeIndex(initialTypeIndex)}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}
            >
              Back to Settings
            </Button>
          </Stack>

          <ConsultationTypeList
            consultationTypes={visibleConsultationTypesWithIndex}
            defaultName={defaultName}
            defaultDescription={defaultDescription}
            defaultEmail={calendarSettings?.email || calendarSettings?.googleConfig?.email || calendarSettings?.outlookConfig?.email || ""}
            defaultDuration={calendarSettings?.meetingDurationMinutes || calendarSettings?.googleConfig?.meetingDurationMinutes || 60}
            defaultManager={calendarSettings?.email || calendarSettings?.googleConfig?.email || "Store Admin"}
            defaultIsConnected={!!(
              (calendarSettings?.googleConfig?.googleOauthTokens || calendarSettings?.defaultConsultationType?.googleOauthTokens) ||
              (calendarSettings?.outlookConfig?.outlookOauthTokens || calendarSettings?.defaultConsultationType?.outlookOauthTokens)
            )}
            defaultIsActive={calendarSettings?.googleConfig?.isActive !== false && calendarSettings?.isActive !== false}
            showDefaultCard={showDefaultSettings}
            onSelectType={(idx) => setViewingTypeIndex(idx)}
            onAddType={handleOpenAddModal}
            onDeleteType={(idx) => {
              if (setupRef.current && typeof setupRef.current.deleteType === 'function') {
                setupRef.current.deleteType(idx);
              }
            }}
            canEdit={permissions.canEdit}
          />

          {/* Hidden Setup instance so modal handler works cleanly */}
          <Box sx={{ display: 'none' }}>
            <Setup
              ref={setupRef}
              canEdit={permissions.canEdit}
              initialSettings={calendarSettings}
              onSettingsUpdate={onSettingsUpdate}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              selectedConsultationTypeTab={0}
              onSelectConsultationTypeTab={() => {}}
            />
          </Box>
        </Box>
      ) : (
        /* PAGE 2: Standard & Dedicated Calendar Settings Page */
        <Box sx={{ width: "100%" }}>
          {/* Header for Settings Page */}
          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            p: 2,
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fff"
          }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton onClick={onBack}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0d3559' }}>
                Calendar Settings
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              {(showDefaultSettings || visibleConsultationTypesWithIndex.length > 0) && (
                <Button
                  variant="outlined"
                  onClick={() => setViewingTypeIndex(null)}
                  sx={{ textTransform: 'none', borderRadius: 1.5, borderColor: '#cbd5e1', color: '#475569' }}
                >
                  View All Types ({(showDefaultSettings ? 1 : 0) + visibleConsultationTypesWithIndex.length})
                </Button>
              )}
              {permissions.canEdit && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddModal}
                  sx={{ bgcolor: '#0d3559', textTransform: 'none', borderRadius: 1.5 }}
                >
                  Add Consultation Type
                </Button>
              )}
            </Stack>
          </Box>

          <Box sx={{ p: 3, maxWidth: 1200, margin: "0 auto", width: "100%" }}>
            <Stack spacing={3}>
              {/* Consultation Type Tabs (if configured and visible to logged in user) */}
              {(showDefaultSettings || visibleConsultationTypesWithIndex.length > 0) && (
                <Paper sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                  <Tabs
                    value={viewingTypeIndex}
                    onChange={(e, val) => setViewingTypeIndex(val)}
                    variant="scrollable"
                    scrollButtons="auto"
                    textColor="primary"
                    indicatorColor="primary"
                  >
                    {showDefaultSettings && <Tab label={defaultName} value={0} />}
                    {visibleConsultationTypesWithIndex.map(({ type, originalIdx }) => (
                      <Tab key={originalIdx} label={type.name || `Type ${originalIdx + 1}`} value={originalIdx + 1} />
                    ))}
                  </Tabs>
                </Paper>
              )}
              {/* Account Management (Authorization) Section at TOP for Selected Type */}
              <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Permission
                  canEdit={permissions.canEdit}
                  canDelete={permissions.canDelete}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  selectedConsultationType={selectedType}
                  onSettingsUpdate={onSettingsUpdate}
                  onBeforeAuthorize={async () => {
                    if (setupRef.current && typeof setupRef.current.saveSettings === 'function') {
                      try {
                        await setupRef.current.saveSettings();
                      } catch (e) {
                        console.warn("Auto-save before authorization warning:", e);
                      }
                    }
                    const email = setupRef.current?.getCurrentEmail?.();
                    return { calendarEmail: email };
                  }}
                />
              </Paper>

              {/* Complete Calendar & Consultation Settings Form */}
              <Paper elevation={0} variant="outlined" sx={{ p: 0, borderRadius: 2, border: 'none' }}>
                <Setup
                  ref={setupRef}
                  canEdit={permissions.canEdit}
                  initialSettings={calendarSettings}
                  onSettingsUpdate={onSettingsUpdate}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  selectedConsultationTypeTab={viewingTypeIndex}
                  onSelectConsultationTypeTab={setViewingTypeIndex}
                />
              </Paper>
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SettingsView;
